"use client";

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Plus,
  TrendingUp,
  TrendingDown,
  Sparkles,
  Trash2,
  ChevronLeft,
  Eye,
  Pencil,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  Calendar,
} from "lucide-react";
import { useStore } from "@/lib/store";
import type {
  ProgressCard,
  ProgressMetric,
  ProgressSourceRef,
} from "@/lib/types";
import { cn, formatDate, formatRelative } from "@/lib/utils";
import { Avatar } from "@/components/ui/Avatar";
import { Markdown } from "@/components/ui/Markdown";
import { generateProgressDraft } from "@/lib/agentDraft";
import { safeExternalUrl } from "@/lib/externalUrl";
import { ProgressSlackSyncButton } from "@/components/slack/ProgressSlackSyncButton";
import { humanOwnerId } from "@/lib/taskOwnership";

export function ProgressView({ canWrite }: { canWrite: boolean }) {
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);
  const allCards = useStore((s) => s.progressCards);
  const allProjects = useStore((s) => s.projects);
  const allIssues = useStore((s) => s.issues);
  const users = useStore((s) => s.users);
  const selectedId = useStore((s) => s.selectedProgressCardId);
  const selectCard = useStore((s) => s.selectProgressCard);
  const createProgressCard = useStore((s) => s.createProgressCard);
  const updateProgressCard = useStore((s) => s.updateProgressCard);
  const deleteProgressCard = useStore((s) => s.deleteProgressCard);

  const cards = useMemo(
    () => allCards.filter((c) => c.workspaceId === activeWorkspaceId),
    [allCards, activeWorkspaceId]
  );
  const projects = useMemo(
    () => allProjects.filter((p) => p.workspaceId === activeWorkspaceId),
    [allProjects, activeWorkspaceId]
  );
  const issues = useMemo(
    () => allIssues.filter((i) => i.workspaceId === activeWorkspaceId),
    [allIssues, activeWorkspaceId]
  );

  const sorted = useMemo(
    () => [...cards].sort((a, b) => b.periodEnd - a.periodEnd),
    [cards]
  );

  const selected = cards.find((c) => c.id === selectedId);

  function handleCreate() {
    if (!canWrite) return;
    const now = Date.now();
    const card = createProgressCard({
      title: `Progress · Week of ${formatDate(now)}`,
      periodStart: now - 7 * 24 * 60 * 60 * 1000,
      periodEnd: now,
      summary: "",
    });
    selectCard(card.id);
  }

  if (selected) {
    return (
      <ProgressCardDetail
        card={selected}
        canWrite={canWrite}
        onClose={() => selectCard(null)}
        onUpdate={(patch) => {
          if (!canWrite) return;
          updateProgressCard(selected.id, patch);
        }}
        onDelete={() => {
          if (!canWrite) return;
          if (confirm("Delete this progress card?")) deleteProgressCard(selected.id);
        }}
        onGenerate={() => {
          if (!canWrite) return;
          const related = issues.filter(
            (i) => selected.projectId && i.projectId === selected.projectId
          );
          const draft = generateProgressDraft({
            title: selected.title,
            projectName: projects.find((p) => p.id === selected.projectId)?.name,
            issues: related,
            periodStart: selected.periodStart,
            periodEnd: selected.periodEnd,
          });
          updateProgressCard(selected.id, {
            summary: draft.summary,
            highlights: draft.highlights,
            blockers: draft.blockers,
            nextSteps: draft.nextSteps,
            metrics: draft.metrics,
            issueIds: draft.issueIds,
          });
        }}
      />
    );
  }

  return (
    <div className="flex-1 overflow-y-auto scrollbar-thin">
      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-[18px] font-semibold">Progress Cards</h2>
            <p className="text-[12.5px] text-[var(--fg-tertiary)] mt-0.5">
              Periodic reports on what shipped, what's blocked, and what's next. Exportable to
              any knowledge base.
            </p>
          </div>
          {canWrite && (
            <button
              onClick={handleCreate}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white text-[12.5px] font-medium transition-colors"
            >
              <Plus size={13} />
              New progress card
            </button>
          )}
        </div>

        {sorted.length === 0 ? (
          <div className="py-20 text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[var(--bg-tertiary)] border border-[var(--border)] flex items-center justify-center">
              <TrendingUp size={20} className="text-[var(--fg-tertiary)]" />
            </div>
            <h3 className="text-[15px] font-semibold mb-1">No progress cards yet</h3>
            <p className="text-[13px] text-[var(--fg-tertiary)]">
              {canWrite
                ? "Create one to summarize what happened this period."
                : "Progress cards will appear here when your team creates them."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {sorted.map((c, i) => (
              <ProgressCardTile
                key={c.id}
                card={c}
                delay={i * 0.04}
                project={projects.find((p) => p.id === c.projectId)}
                author={users.find((u) => u.id === c.authorId)}
                onOpen={() => selectCard(c.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ProgressCardTile({
  card,
  project,
  author,
  onOpen,
  delay,
}: {
  card: ProgressCard;
  project?: { icon: string; name: string; color: string };
  author?: { name: string; initials: string; color: string; id: string; email: string };
  onOpen: () => void;
  delay: number;
}) {
  const statusColor = card.status === "published" ? "var(--green)" : "var(--yellow)";
  const sourceRefs = card.sourceRefs ?? [];
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="rounded-xl border border-[var(--border)] bg-[var(--bg-secondary)] hover:border-[var(--border-strong)] transition-colors overflow-hidden"
    >
      <button
        onClick={onOpen}
        className={cn(
          "w-full text-left p-5",
          sourceRefs.length > 0 && "pb-3"
        )}
      >
        <div className="flex items-center gap-2 mb-3">
          {project && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px]"
              style={{ background: `${project.color}22`, color: project.color }}
            >
              {project.icon} {project.name}
            </span>
          )}
          <SourceBadge source={card.source ?? "manual"} />
          <span
            className="ml-auto text-[10.5px] font-medium px-1.5 py-0.5 rounded-full"
            style={{
              background: `color-mix(in srgb, ${statusColor} 18%, transparent)`,
              color: statusColor,
            }}
          >
            {card.status}
          </span>
        </div>
        <h3 className="text-[15px] font-semibold mb-1 tracking-tight">{card.title}</h3>
        <div className="flex items-center gap-1.5 text-[11.5px] text-[var(--fg-tertiary)] mb-3">
          <Calendar size={11} />
          {formatDate(card.periodStart)} – {formatDate(card.periodEnd)}
        </div>
        <p className="text-[12.5px] text-[var(--fg-secondary)] line-clamp-3 mb-3">
          {card.summary || (
            <span className="text-[var(--fg-muted)] italic">No summary yet</span>
          )}
        </p>
        {card.metrics.length > 0 && (
          <div className="grid grid-cols-3 gap-3 py-3 border-t border-[var(--border)] mb-3">
            {card.metrics.slice(0, 3).map((m, i) => (
              <MetricCell key={i} metric={m} />
            ))}
          </div>
        )}
        <div className="flex items-center justify-between text-[11px] text-[var(--fg-muted)]">
          <div className="flex items-center gap-1.5">
            <Avatar user={author} size={16} />
            <span>{author?.name}</span>
          </div>
          <span>Updated {formatRelative(card.updatedAt)}</span>
        </div>
      </button>
      {sourceRefs.length > 0 && (
        <div className="px-5 pb-4">
          <EvidenceLinks refs={sourceRefs} compact />
        </div>
      )}
    </motion.div>
  );
}

function MetricCell({ metric }: { metric: ProgressMetric }) {
  const isPositive = metric.change?.startsWith("+");
  const isNegative = metric.change?.startsWith("-");
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-wide text-[var(--fg-muted)]">
        {metric.label}
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="text-[16px] font-semibold">{metric.value}</span>
        {metric.change && metric.change !== "—" && (
          <span
            className="text-[10.5px] inline-flex items-center gap-0.5"
            style={{ color: isPositive ? "var(--green)" : isNegative ? "var(--red)" : "var(--fg-muted)" }}
          >
            {isPositive && <TrendingUp size={10} />}
            {isNegative && <TrendingDown size={10} />}
            {metric.change}
          </span>
        )}
      </div>
    </div>
  );
}

function ProgressCardDetail({
  card,
  canWrite,
  onClose,
  onUpdate,
  onDelete,
  onGenerate,
}: {
  card: ProgressCard;
  canWrite: boolean;
  onClose: () => void;
  onUpdate: (patch: Partial<ProgressCard>) => void;
  onDelete: () => void;
  onGenerate: () => void;
}) {
  const allProjectsD = useStore((s) => s.projects);
  const allIssuesD = useStore((s) => s.issues);
  const users = useStore((s) => s.users);
  const projects = useMemo(
    () => allProjectsD.filter((p) => p.workspaceId === card.workspaceId),
    [allProjectsD, card.workspaceId]
  );
  const issues = useMemo(
    () => allIssuesD.filter((i) => i.workspaceId === card.workspaceId),
    [allIssuesD, card.workspaceId]
  );
  const [mode, setMode] = useState<"edit" | "preview">("preview");
  const visibleMode = canWrite ? mode : "preview";

  const project = projects.find((p) => p.id === card.projectId);
  const linked = issues.filter((i) => card.issueIds.includes(i.id));
  const sourceRefs = card.sourceRefs ?? [];

  function updateList<K extends "highlights" | "blockers" | "nextSteps">(
    field: K,
    idx: number,
    value: string
  ) {
    if (!canWrite) return;
    const arr = [...card[field]];
    arr[idx] = value;
    onUpdate({ [field]: arr } as Partial<ProgressCard>);
  }
  function addListItem(field: "highlights" | "blockers" | "nextSteps") {
    if (!canWrite) return;
    onUpdate({ [field]: [...card[field], ""] } as Partial<ProgressCard>);
  }
  function removeListItem(field: "highlights" | "blockers" | "nextSteps", idx: number) {
    if (!canWrite) return;
    onUpdate({ [field]: card[field].filter((_, i) => i !== idx) } as Partial<ProgressCard>);
  }
  function updateMetric(idx: number, patch: Partial<ProgressMetric>) {
    if (!canWrite) return;
    const arr = card.metrics.map((m, i) => (i === idx ? { ...m, ...patch } : m));
    onUpdate({ metrics: arr });
  }
  function addMetric() {
    if (!canWrite) return;
    onUpdate({ metrics: [...card.metrics, { label: "Metric", value: "0", change: "" }] });
  }
  function removeMetric(idx: number) {
    if (!canWrite) return;
    onUpdate({ metrics: card.metrics.filter((_, i) => i !== idx) });
  }

  return (
    <div className="flex-1 overflow-y-auto scrollbar-thin">
      <div className="max-w-[880px] mx-auto px-8 py-6">
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[12px] text-[var(--fg-tertiary)] hover:bg-[var(--bg-hover)]"
          >
            <ChevronLeft size={13} />
            Back to cards
          </button>
          <div className="ml-auto flex items-center gap-1">
            {canWrite && (
              <>
                <button
                  onClick={onGenerate}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[12px] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] transition-colors"
                  title="Generate from linked project tasks"
                >
                  <Sparkles size={12} />
                  Draft with AI
                </button>
                <div className="flex items-center rounded-md border border-[var(--border)] overflow-hidden">
                  <button
                    onClick={() => setMode("edit")}
                    className={cn(
                      "px-2 py-1 text-[11.5px] transition-colors",
                      mode === "edit"
                        ? "bg-[var(--bg-active)] text-[var(--fg)]"
                        : "text-[var(--fg-tertiary)] hover:bg-[var(--bg-hover)]"
                    )}
                    title="Edit"
                  >
                    <Pencil size={12} />
                  </button>
                  <button
                    onClick={() => setMode("preview")}
                    className={cn(
                      "px-2 py-1 text-[11.5px] border-l border-[var(--border)] transition-colors",
                      mode === "preview"
                        ? "bg-[var(--bg-active)] text-[var(--fg)]"
                        : "text-[var(--fg-tertiary)] hover:bg-[var(--bg-hover)]"
                    )}
                    title="Preview"
                  >
                    <Eye size={12} />
                  </button>
                </div>
                <button
                  onClick={() =>
                    onUpdate({ status: card.status === "published" ? "draft" : "published" })
                  }
                  className={cn(
                    "px-2 py-1 rounded-md text-[11.5px] font-medium transition-colors",
                    card.status === "published"
                      ? "bg-[var(--green)]/18 text-[var(--green)]"
                      : "bg-[var(--yellow)]/18 text-[var(--yellow)]"
                  )}
                >
                  {card.status === "published" ? "Published" : "Draft"}
                </button>
                <ProgressSlackSyncButton card={card} canWrite={canWrite} />
                <button
                  onClick={onDelete}
                  className="w-7 h-7 rounded hover:bg-[var(--bg-hover)] flex items-center justify-center text-[var(--fg-tertiary)] transition-colors"
                  aria-label="Delete progress card"
                >
                  <Trash2 size={13} />
                </button>
              </>
            )}
            {!canWrite && (
              <span
                className={cn(
                  "px-2 py-1 rounded-md text-[11.5px] font-medium",
                  card.status === "published"
                    ? "bg-[var(--green)]/18 text-[var(--green)]"
                    : "bg-[var(--yellow)]/18 text-[var(--yellow)]"
                )}
              >
                {card.status === "published" ? "Published" : "Draft"}
              </span>
            )}
          </div>
        </div>

        {canWrite ? (
          <input
            value={card.title}
            onChange={(e) => onUpdate({ title: e.target.value })}
            className="w-full bg-transparent outline-none text-[26px] font-bold tracking-tight mb-2"
          />
        ) : (
          <h1 className="text-[26px] font-bold tracking-tight mb-2">{card.title}</h1>
        )}

        <div
          className={cn(
            "flex items-center gap-2 flex-wrap text-[11.5px] text-[var(--fg-tertiary)]",
            sourceRefs.length > 0 ? "mb-3" : "mb-6"
          )}
        >
          <Calendar size={12} />
          {canWrite ? (
            <input
              type="date"
              value={new Date(card.periodStart).toISOString().slice(0, 10)}
              onChange={(e) =>
                onUpdate({ periodStart: new Date(e.target.value).getTime() })
              }
              className="bg-transparent border border-[var(--border)] rounded px-1.5 py-0.5 text-[11.5px]"
            />
          ) : (
            <span>{formatDate(card.periodStart)}</span>
          )}
          <span>→</span>
          {canWrite ? (
            <input
              type="date"
              value={new Date(card.periodEnd).toISOString().slice(0, 10)}
              onChange={(e) => onUpdate({ periodEnd: new Date(e.target.value).getTime() })}
              className="bg-transparent border border-[var(--border)] rounded px-1.5 py-0.5 text-[11.5px]"
            />
          ) : (
            <span>{formatDate(card.periodEnd)}</span>
          )}
          {project && (
            <>
              <span className="text-[var(--fg-muted)]">·</span>
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded"
                style={{ background: `${project.color}22`, color: project.color }}
              >
                {project.icon} {project.name}
              </span>
            </>
          )}
          {canWrite && (
            <select
              value={card.projectId || ""}
              onChange={(e) => onUpdate({ projectId: e.target.value || null })}
              className="bg-transparent border border-[var(--border)] rounded px-1.5 py-0.5 text-[11.5px] text-[var(--fg-secondary)]"
            >
              <option value="">No project</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}
          {!canWrite && !project && <span>No project</span>}
          <SourceBadge source={card.source ?? "manual"} />
        </div>

        {sourceRefs.length > 0 && (
          <div className="mb-6 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-3 py-2.5">
            <EvidenceLinks refs={sourceRefs} />
          </div>
        )}

        <Section title="Summary">
          {visibleMode === "edit" ? (
            <textarea
              value={card.summary}
              onChange={(e) => onUpdate({ summary: e.target.value })}
              placeholder="One-paragraph summary of this period."
              className="w-full bg-[var(--bg-secondary)] border border-[var(--border)] rounded-md p-3 text-[13px] text-[var(--fg-secondary)] outline-none focus:border-[var(--accent)] resize-none min-h-[80px]"
            />
          ) : (
            <div className="rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] p-4">
              <Markdown content={card.summary || "_No summary yet._"} />
            </div>
          )}
        </Section>

        {card.metrics.length > 0 && (
          <Section title="Metrics">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {card.metrics.map((m, i) =>
                visibleMode === "edit" ? (
                  <div
                    key={i}
                    className="rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] p-3"
                  >
                    <input
                      value={m.label}
                      onChange={(e) => updateMetric(i, { label: e.target.value })}
                      className="w-full bg-transparent outline-none text-[10.5px] uppercase tracking-wide text-[var(--fg-muted)] mb-1"
                    />
                    <div className="flex gap-1.5 items-baseline">
                      <input
                        value={m.value}
                        onChange={(e) => updateMetric(i, { value: e.target.value })}
                        className="w-20 bg-transparent outline-none text-[16px] font-semibold"
                      />
                      <input
                        value={m.change || ""}
                        placeholder="+0%"
                        onChange={(e) => updateMetric(i, { change: e.target.value })}
                        className="w-16 bg-transparent outline-none text-[10.5px] text-[var(--fg-tertiary)]"
                      />
                    </div>
                    <button
                      onClick={() => removeMetric(i)}
                      className="text-[10px] text-[var(--fg-muted)] hover:text-[var(--red)] mt-1"
                    >
                      remove
                    </button>
                  </div>
                ) : (
                  <div
                    key={i}
                    className="rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] p-3"
                  >
                    <MetricCell metric={m} />
                  </div>
                )
              )}
            </div>
            {visibleMode === "edit" && (
              <button
                onClick={addMetric}
                className="mt-2 text-[11.5px] text-[var(--fg-secondary)] hover:text-[var(--fg)] inline-flex items-center gap-1"
              >
                <Plus size={11} /> Add metric
              </button>
            )}
          </Section>
        )}
        {visibleMode === "edit" && card.metrics.length === 0 && (
          <Section title="Metrics">
            <button
              onClick={addMetric}
              className="text-[12px] text-[var(--fg-secondary)] hover:text-[var(--fg)] inline-flex items-center gap-1 px-2 py-1 rounded border border-dashed border-[var(--border-strong)]"
            >
              <Plus size={11} /> Add metric
            </button>
          </Section>
        )}

        <EditableList
          title="Highlights"
          icon={<CheckCircle2 size={13} className="text-[var(--green)]" />}
          items={card.highlights}
          mode={visibleMode}
          onChange={(i, v) => updateList("highlights", i, v)}
          onAdd={() => addListItem("highlights")}
          onRemove={(i) => removeListItem("highlights", i)}
        />

        <EditableList
          title="Blockers"
          icon={<AlertTriangle size={13} className="text-[var(--orange)]" />}
          items={card.blockers}
          mode={visibleMode}
          onChange={(i, v) => updateList("blockers", i, v)}
          onAdd={() => addListItem("blockers")}
          onRemove={(i) => removeListItem("blockers", i)}
        />

        <EditableList
          title="Next steps"
          icon={<ArrowRight size={13} className="text-[var(--blue)]" />}
          items={card.nextSteps}
          mode={visibleMode}
          onChange={(i, v) => updateList("nextSteps", i, v)}
          onAdd={() => addListItem("nextSteps")}
          onRemove={(i) => removeListItem("nextSteps", i)}
        />

        {linked.length > 0 && (
          <Section title="Linked tasks">
            <div className="rounded-md border border-[var(--border)] divide-y divide-[var(--border)]">
              {linked.map((iss) => {
                const assignee = users.find((u) => u.id === humanOwnerId(iss));
                return (
                  <div
                    key={iss.id}
                    className="flex items-center gap-2 px-3 py-2 text-[12.5px]"
                  >
                    <span className="font-mono text-[11px] text-[var(--fg-muted)] w-14">
                      {iss.identifier}
                    </span>
                    <span className="truncate flex-1">{iss.title}</span>
                    <span className="text-[11px] text-[var(--fg-tertiary)]">
                      {iss.status}
                    </span>
                    <Avatar user={assignee} size={16} />
                  </div>
                );
              })}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}

const SOURCE_META: Record<
  ProgressCard["source"],
  { label: string; color: string }
> = {
  manual: { label: "Manual", color: "var(--fg-tertiary)" },
  agent: { label: "Agent", color: "var(--accent)" },
  github: { label: "GitHub", color: "var(--green)" },
  slack: { label: "Slack", color: "var(--purple)" },
  import: { label: "Imported", color: "var(--blue)" },
};

const SOURCE_REF_LABEL: Record<ProgressSourceRef["type"], string> = {
  commit: "Commit",
  pull_request: "PR",
  deployment: "Deploy",
  release: "Release",
  workflow: "Workflow",
  link: "Link",
};

function SourceBadge({ source }: { source: ProgressCard["source"] }) {
  if (source === "manual") return null;
  const meta = SOURCE_META[source] ?? SOURCE_META.import;
  return (
    <span
      className="inline-flex items-center rounded border px-1.5 py-0.5 text-[10.5px] font-medium leading-none"
      style={{
        color: meta.color,
        background: `color-mix(in srgb, ${meta.color} 12%, transparent)`,
        borderColor: `color-mix(in srgb, ${meta.color} 28%, transparent)`,
      }}
      title={`Progress source: ${meta.label}`}
    >
      {meta.label}
    </span>
  );
}

function EvidenceLinks({
  refs,
  compact = false,
}: {
  refs: readonly ProgressSourceRef[];
  compact?: boolean;
}) {
  const visible = compact ? refs.slice(0, 2) : refs;
  const remaining = refs.length - visible.length;
  return (
    <div className="flex items-center gap-1.5 flex-wrap min-w-0">
      <span className="text-[10.5px] uppercase tracking-wide text-[var(--fg-muted)] font-medium shrink-0">
        Evidence
      </span>
      {visible.map((ref, index) => (
        <EvidenceLink
          key={`${ref.type}:${ref.url}:${index}`}
          evidence={ref}
          compact={compact}
        />
      ))}
      {remaining > 0 && (
        <span className="text-[10.5px] text-[var(--fg-muted)]">
          +{remaining} more
        </span>
      )}
    </div>
  );
}

function EvidenceLink({
  evidence,
  compact,
}: {
  evidence: ProgressSourceRef;
  compact: boolean;
}) {
  const href = safeExternalUrl(evidence.url);
  const content = (
    <>
      <span className="text-[10.5px] uppercase tracking-wide text-[var(--fg-muted)]">
        {SOURCE_REF_LABEL[evidence.type]}
      </span>
      <span className="truncate max-w-[180px]">{evidence.label}</span>
      {!compact && evidence.sha && (
        <span className="font-mono text-[10px] text-[var(--fg-muted)]">
          {evidence.sha.slice(0, 7)}
        </span>
      )}
      {!compact && evidence.status && (
        <span className="text-[10px] text-[var(--fg-tertiary)]">
          {evidence.status}
        </span>
      )}
      {href && <span aria-hidden="true">↗</span>}
    </>
  );
  const className =
    "inline-flex min-w-0 items-center gap-1 rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-2 py-1 text-[11px] text-[var(--fg-secondary)]";

  if (!href) {
    return (
      <span
        className={cn(className, "opacity-60")}
        title="Evidence URL is unavailable or unsafe"
      >
        {content}
      </span>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        className,
        "hover:bg-[var(--bg-hover)] hover:border-[var(--border-strong)] transition-colors"
      )}
      title={`Open ${evidence.label}`}
    >
      {content}
    </a>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-6">
      <h3 className="text-[11px] uppercase tracking-wide text-[var(--fg-muted)] font-medium mb-2">
        {title}
      </h3>
      {children}
    </div>
  );
}

function EditableList({
  title,
  icon,
  items,
  mode,
  onChange,
  onAdd,
  onRemove,
}: {
  title: string;
  icon: React.ReactNode;
  items: string[];
  mode: "edit" | "preview";
  onChange: (i: number, v: string) => void;
  onAdd: () => void;
  onRemove: (i: number) => void;
}) {
  return (
    <Section title={title}>
      {items.length === 0 && mode === "preview" && (
        <div className="text-[12px] text-[var(--fg-muted)] italic">None.</div>
      )}
      <div className="space-y-1.5">
        {items.map((item, i) => (
          <div key={i} className="flex items-start gap-2 group">
            <span className="mt-1 shrink-0">{icon}</span>
            {mode === "edit" ? (
              <>
                <input
                  value={item}
                  onChange={(e) => onChange(i, e.target.value)}
                  className="flex-1 bg-transparent border-b border-transparent focus:border-[var(--border-strong)] outline-none text-[13px] text-[var(--fg-secondary)] py-0.5"
                />
                <button
                  onClick={() => onRemove(i)}
                  className="opacity-0 group-hover:opacity-100 text-[10px] text-[var(--fg-muted)] hover:text-[var(--red)]"
                >
                  <Trash2 size={11} />
                </button>
              </>
            ) : (
              <div className="flex-1 text-[13px] text-[var(--fg-secondary)] leading-relaxed">
                {item}
              </div>
            )}
          </div>
        ))}
      </div>
      {mode === "edit" && (
        <button
          onClick={onAdd}
          className="mt-2 text-[11.5px] text-[var(--fg-secondary)] hover:text-[var(--fg)] inline-flex items-center gap-1"
        >
          <Plus size={11} /> Add {title.toLowerCase().replace(/s$/, "")}
        </button>
      )}
    </Section>
  );
}
